import React, { useState } from 'react';
import { Box, Typography, IconButton, CircularProgress, Alert } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import { usePromotedVendors } from '../../hooks/usePromotedVendors';
import AddVendorModal from '../../components/Promotions/AddVendorModal';
import { toast } from 'react-toastify';
import { useCategoryLabelMaps } from '../../hooks/useCategoryLabelMaps';
import { formatCategoryLabel } from '../../utils/categoryLabels';

interface PromotedVendorsProps {
  searchQuery: string;
  showAddVendorModal: boolean;
  onCloseAddVendorModal: () => void;
}

const PromotedVendors: React.FC<PromotedVendorsProps> = ({ 
  searchQuery, 
  showAddVendorModal, 
  onCloseAddVendorModal 
}) => {  
  const { maps: categoryLabels } = useCategoryLabelMaps();
  const { 
    promotedVendors, 
    loading, 
    error, 
    addVendorToPromotion,
    removeVendorFromPromotion,
    reorderVendors 
  } = usePromotedVendors();

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Filter vendors based on search query
  const filteredVendors = promotedVendors.filter(vendor => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    // Get display values
    const displayName = vendor.vendorName || vendor.vendorId || '';
    const displayEmail = vendor.vendorEmail || '';
    const displayCategory = formatCategoryLabel(
      vendor.vendorCategory || '',
      categoryLabels?.typeByKey || {},
    );
    const displayServices = vendor.vendorServices || '';
    
    return (
      displayName.toLowerCase().includes(query) ||
      displayEmail.toLowerCase().includes(query) ||
      displayCategory.toLowerCase().includes(query) ||
      displayServices.toLowerCase().includes(query)
    );
  });

  // Handle add vendor
  const handleAddVendor = async (vendorId: string) => {
    try {
      await addVendorToPromotion({ vendorId });
      toast.success('Vendor added to promotions successfully');
      onCloseAddVendorModal();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to add vendor to promotions';
      toast.error(errorMessage);
      throw err; // Re-throw to let modal handle it
    }
  };

  // Handle remove vendor
  const handleRemoveVendor = async (vendorId: string) => {
    try {
      await removeVendorFromPromotion(vendorId);
      toast.success('Vendor removed from promotions successfully');
    } catch (err) {
      toast.error('Failed to remove vendor from promotions');
    }
  };

  // Handle drag start
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  // Handle drop
  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    // Reorder locally first for immediate feedback
    const reordered = [...filteredVendors];
    const [draggedItem] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, draggedItem);

    try {
      // Send new order to backend
      const vendorIds = reordered.map(v => v.vendorId);
      await reorderVendors(vendorIds);
      
      toast.success('Vendors reordered successfully');
    } catch (err) {
      toast.error('Failed to reorder vendors');
    }

    setDraggedIndex(null);
  };

  // Render content based on state
  let content;
  
  // Show loading state
  if (loading && promotedVendors.length === 0) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }
  // Show error state
  else if (error && promotedVendors.length === 0) {
    content = (
      <Box sx={{ padding: 'var(--palette-light-4-value)' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  // Show empty state
  else if (filteredVendors.length === 0) {
    content = (
      <Box sx={{ padding: 'var(--palette-light-4-value)' }}>
        <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)' }}>
          {searchQuery ? 'No promoted vendors match your search' : 'No promoted vendors yet'}
        </Typography>
      </Box>
    );
  }
  // Show vendors list
  else {
    content = (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0', 
        flex: 1, 
        overflow: 'auto',
        paddingRight: 'var(--palette-light-4-value)',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
          borderRadius: '4px',
        },
      }}>
        {filteredVendors.map((vendor, index) => {
          // Extract display values with proper fallbacks
          const displayName = vendor.vendorName || vendor.vendorId || 'Unknown Vendor';
          const displayEmail = vendor.vendorEmail || '';
          const displayCategory = formatCategoryLabel(
            vendor.vendorCategory || '',
            categoryLabels?.typeByKey || {},
          );
          const displayServices = vendor.vendorServices || '';
          const displayAvatar = vendor.vendorAvatar || '';
          
          // Determine if we should show category/services line
          const hasCategoryOrServices = displayCategory || displayServices;
          
          return (
            <Box
              key={vendor.vendorId}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--palette-light-6-value)',
                padding: 'var(--palette-light-4-value) 0',
                borderBottom: index < filteredVendors.length - 1 ? '1px solid var(--palette-light-border-primary-value-hex)' : 'none',
                opacity: draggedIndex === index ? 0.5 : 1,
                cursor: 'move',
              }}
            >
              {/* Drag Handle */}
              <IconButton
                sx={{
                  width: 'var(--palette-light-6-value)',
                  height: 'var(--palette-light-6-value)',
                  padding: 0,
                  cursor: 'grab',
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
              >
                <DragIndicatorIcon sx={{ color: 'var(--palette-light-text-primary-value-hex)' }} />
              </IconButton>

              {/* Avatar or Placeholder - ALWAYS SHOW */}
              {displayAvatar ? (
                <Box
                  component="img"
                  src={displayAvatar}
                  alt={displayName}
                  sx={{
                    width: '160px',
                    height: '81.45px',
                    borderRadius: 'var(--palette-light-2-value)',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                  onError={(e: any) => {
                    // If image fails to load, replace with placeholder
                    e.target.style.display = 'none';
                    const placeholder = e.target.nextSibling;
                    if (placeholder) {
                      placeholder.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              
              {/* Placeholder - show if no avatar OR if image fails */}
              <Box
                sx={{
                  width: '160px',
                  height: '81.45px',
                  borderRadius: 'var(--palette-light-2-value)',
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                  display: displayAvatar ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ color: 'var(--palette-light-text-tertiary-value-hex)', fontSize: '12px' }}>
                  No Image
                </Typography>
              </Box>

              {/* Vendor Info - 3 Lines */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)', flex: 1 }}>
                {/* Line 1: Vendor Name (ALWAYS SHOW) */}
                <Typography 
                  sx={{ 
                    fontSize: 'var(--palette-light-font-size-default-value)', 
                    fontWeight: 700, 
                    color: 'var(--palette-light-text-primary-value-hex)' 
                  }}
                >
                  {displayName}
                </Typography>
                
                {/* Line 2: Email (show if exists, otherwise show empty line for consistency) */}
                {displayEmail ? (
                  <Typography 
                    sx={{ 
                      fontSize: 'var(--palette-light-font-size-small-value)', 
                      color: 'var(--palette-light-text-secondary-value-hex)' 
                    }}
                  >
                    {displayEmail}
                  </Typography>
                ) : (
                  <Typography 
                    sx={{ 
                      fontSize: 'var(--palette-light-font-size-small-value)', 
                      color: 'var(--palette-light-text-tertiary-value-hex)',
                      fontStyle: 'italic'
                    }}
                  >
                    No email
                  </Typography>
                )}
                
                {/* Line 3: Category • Services (show if exists) */}
                {hasCategoryOrServices ? (
                  <Box sx={{ display: 'flex', gap: 'var(--palette-light-2-value)', alignItems: 'center', flexWrap: 'wrap' }}>
                    {displayCategory && (
                      <Typography 
                        sx={{ 
                          fontSize: 'var(--palette-light-font-size-small-value)', 
                          color: 'var(--palette-light-text-primary-value-hex)' 
                        }}
                      >
                        {displayCategory}
                      </Typography>
                    )}
                    {displayCategory && displayServices && (
                      <Typography 
                        sx={{ 
                          fontSize: 'var(--palette-light-font-size-small-value)', 
                          color: 'var(--palette-light-text-primary-value-hex)' 
                        }}
                      >
                        •
                      </Typography>
                    )}
                    {displayServices && (
                      <Typography 
                        sx={{ 
                          fontSize: 'var(--palette-light-font-size-small-value)', 
                          color: 'var(--palette-light-text-primary-value-hex)' 
                        }}
                      >
                        {displayServices}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography 
                    sx={{ 
                      fontSize: 'var(--palette-light-font-size-small-value)', 
                      color: 'var(--palette-light-text-tertiary-value-hex)',
                      fontStyle: 'italic'
                    }}
                  >
                    No category or services
                  </Typography>
                )}
              </Box>

              {/* Remove Button */}
              <Box
                onClick={() => handleRemoveVendor(vendor.vendorId)}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '56px',
                  height: '56px',
                  border: '1px solid var(--palette-light-brand-primary-value-hex)',
                  borderRadius: 'var(--palette-light-4-value)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                  },
                }}
              >
                <CloseIcon sx={{ color: 'var(--palette-light-brand-primary-value-hex)', fontSize: '20px' }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }
  
  return (
    <>
      {/* Add Vendor Modal - ALWAYS RENDERED */}
      <AddVendorModal
        open={showAddVendorModal}
        onClose={onCloseAddVendorModal}
        onAdd={handleAddVendor}
        excludeVendorIds={promotedVendors.map(v => v.vendorId)}
      />

      {/* Content */}
      {content}
    </>
  );
};

export default PromotedVendors;

